import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../_shared/ProjectHeader/ProjectHeader";
import {
  STRATEGY_CANVAS,
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_REVIEW,
  STRATEGY_CANVAS_LIVE_REVIEW_WAITING,
  STRATEGY_CANVAS_VIEW,
  XRAY_USER_SCORE,
} from "../../../data/RouteUrls";
import { DownloadIcon, ExternalLinkIcon } from "./StrategyCanvasHelpers";
import StrategyCanvasDocuments from "./StrategyCanvasDocuments";
import StrategyCanvasBusinessContext from "./StrategyCanvasBusinessContext";
import "./StrategyCanvas.scss";
import "./StrategyCanvasBrief.scss";
import { HeaderContext } from "../../../HeaderContext";
import { breadCrumbsArray } from "../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../_shared/_redux/ProjectActions";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "./_redux/StrategyCanvasStore";

const TAB_SUMMARY = "Summary";
const TAB_XRAY = "X-Ray Report";
const TAB_BUSINESS_CONTEXT = "Business Context";
const TAB_CEO_CANVAS = "How the CEO built their canvas";
const TAB_DOCUMENTS = "Documents";

const TABS = [
  TAB_SUMMARY,
  TAB_XRAY,
  // TAB_BUSINESS_CONTEXT,
  TAB_CEO_CANVAS,
  TAB_DOCUMENTS,
];

const LOCKED_STATES = ["LOCKED"];

const selectBriefState = (state) => ({
  clients: state.clients,
  canvasStatusByUniqueId: state.canvasStatusByUniqueId,
  summaryByUniqueId: state.summaryByUniqueId,
  businessContextByUniqueId: state.businessContextByUniqueId,
  documentsByUniqueId: state.documentsByUniqueId,
  howCeoBuiltByUniqueId: state.howCeoBuiltByUniqueId,
  xrayByUniqueId: state.xrayByUniqueId,
  summaryLoading: state.summaryLoading,
  businessContextLoading: state.businessContextLoading,
  documentsLoading: state.documentsLoading,
  howCeoBuiltLoading: state.howCeoBuiltLoading,
  xrayLoading: state.xrayLoading,
  summaryDownloading: state.summaryDownloading,
  howCeoBuiltDownloading: state.howCeoBuiltDownloading,
});

const splitIntoParagraphs = (story = "") =>
  story
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const SummaryCard = ({ paragraphs }) => (
  <div className="scb-summary-card">
    {paragraphs.map((paragraph, index) => (
      <p key={index}>{paragraph}</p>
    ))}
  </div>
);

// Shimmer placeholder shown while a tab's data is being fetched.
const BriefLoadingCard = () => (
  <div className="scb-summary-card">
    {Array.from({ length: 5 }).map((_, index) => (
      <div className="sc-skeleton-block scb-skeleton-line" key={index} />
    ))}
  </div>
);

const StrategyCanvasBrief = () => {
  const history = useHistory();
  const { briefId } = useParams();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const {
    clients,
    canvasStatusByUniqueId,
    summaryByUniqueId,
    businessContextByUniqueId,
    documentsByUniqueId,
    howCeoBuiltByUniqueId,
    xrayByUniqueId,
    summaryLoading,
    businessContextLoading,
    documentsLoading,
    howCeoBuiltLoading,
    xrayLoading,
    summaryDownloading,
    howCeoBuiltDownloading,
  } = useStrategyCanvasStoreSelector(selectBriefState);

  const client = (clients || []).find((sme) => sme.briefId === briefId);
  const companyName = client?.name || "";
  const canvasStatus = canvasStatusByUniqueId[briefId];
  const canvasState = canvasStatus?.canvas_state;
  const isLocked = LOCKED_STATES.includes(canvasState);
  const isLiveReviewInProgress = canvasState === "LIVE_REVIEW_IN_PROGRESS";
  // Once the review has been sent to the CEO the next thing the coach does is
  // run the session, so the CTA reads "Begin live review". It still routes
  // into Review Mode: that screen renders the read-only submitted state and
  // carries the actual "Start Live Review" action in its footer.
  const isReviewSubmitted = canvasState === "COACH_REVIEW_SUBMITTED";
  const canBeginReview = canvasState === "COACH_REVIEW" || isReviewSubmitted;
  const summary = summaryByUniqueId[briefId];
  const businessContext = businessContextByUniqueId[briefId];
  const documents = documentsByUniqueId[briefId];
  const howCeoBuilt = howCeoBuiltByUniqueId[briefId];
  const xray = xrayByUniqueId[briefId];

  const handleOpenXrayReport = () => {
    if (!xray?.user_id) return;
    // Coming from the Strategy Canvas, the X-Ray should open straight into
    // the Strategy discipline rather than its usual "Overall" default -
    // StatsCard.js reads this filter param and selects that tab up front.
    window.open(
      `${XRAY_USER_SCORE.replace(":userId", xray.user_id)}?filter=strategy`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleBeginCanvasReview = async () => {
    await useStrategyCanvasStore.getState().enterReview(briefId);
    history.push(STRATEGY_CANVAS_REVIEW.replace(":briefId", briefId));
  };

  // Same destination, minus the review/enter call: that endpoint stamps
  // coach_review_started_at for the first open, and the review is already
  // finished and submitted by this point.
  const handleOpenSubmittedReview = () => {
    history.push(STRATEGY_CANVAS_REVIEW.replace(":briefId", briefId));
  };

  const handleStartLiveReview = () => {
    history.push(
      STRATEGY_CANVAS_LIVE_REVIEW_WAITING.replace(":briefId", briefId),
    );
  };

  const handleViewStrategyCanvas = () => {
    history.push(STRATEGY_CANVAS_VIEW.replace(":briefId", briefId));
  };

  // The header CTA is purely a function of canvas_state. Resolved in one place
  // rather than as nested ternaries in the markup, since it now covers four
  // states.
  const resolveCta = () => {
    if (isLocked) {
      return { label: "View Strategy Canvas", onClick: handleViewStrategyCanvas };
    }
    if (isLiveReviewInProgress) {
      return { label: "Continue live review", onClick: handleStartLiveReview };
    }
    if (isReviewSubmitted) {
      return { label: "Start live review", onClick: handleOpenSubmittedReview };
    }
    return { label: "Start canvas review", onClick: handleBeginCanvasReview };
  };

  const cta = resolveCta();
  const ctaDisabled = !isLocked && !isLiveReviewInProgress && !canBeginReview;

  const handleDownloadSummaryPdf = () => {
    useStrategyCanvasStore.getState().downloadSummaryPdf(briefId);
  };

  const handleDownloadCeoJourneyPdf = () => {
    useStrategyCanvasStore.getState().downloadCeoJourneyPdf(briefId);
  };

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Strategy Canvas Brief";
    HeaderMetaData.setHeaderMetadata({
      headerTitle: "Strategy Canvas",
    });
    if (!useStrategyCanvasStore.getState().clients) {
      useStrategyCanvasStore.getState().fetchDashboard();
    }
    useStrategyCanvasStore.getState().fetchCanvasStatus(briefId);
    // eslint-disable-next-line
  }, [briefId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(STRATEGY_CANVAS_BRIEF, [
        breadCrumbsArray[STRATEGY_CANVAS][0],
        {
          title: companyName,
          url: "",
        },
      ]),
    );
  }, [companyName]);

  useEffect(() => {
    const store = useStrategyCanvasStore.getState();
    if (activeTab === TAB_SUMMARY) store.fetchSummary(briefId);
    else if (activeTab === TAB_BUSINESS_CONTEXT)
      store.fetchBusinessContext(briefId);
    else if (activeTab === TAB_DOCUMENTS) store.fetchDocuments(briefId);
    else if (activeTab === TAB_CEO_CANVAS) store.fetchHowCeoBuilt(briefId);
    else if (activeTab === TAB_XRAY) store.fetchXray(briefId);
  }, [activeTab, briefId]);

  const renderSummaryTab = () => {
    if (summaryLoading || !summary) return <BriefLoadingCard />;
    if (!summary.story?.story) {
      return (
        <div className="scb-empty-state">
          No summary generated yet for this canvas.
        </div>
      );
    }
    return (
      <SummaryCard paragraphs={splitIntoParagraphs(summary.story.story)} />
    );
  };

  const renderBusinessContextTab = () => {
    if (businessContextLoading || !businessContext) return <BriefLoadingCard />;
    return <StrategyCanvasBusinessContext layers={businessContext.layers} />;
  };

  const renderDocumentsTab = () => {
    if (documentsLoading || !documents) return <BriefLoadingCard />;
    return <StrategyCanvasDocuments blocks={documents.blocks} />;
  };

  const renderCeoCanvasTab = () => {
    if (howCeoBuiltLoading) return <BriefLoadingCard />;
    const narrativeText =
      howCeoBuilt?.narrative?.narrative ||
      howCeoBuilt?.story?.story ||
      (typeof howCeoBuilt === "string" ? howCeoBuilt : null);
    if (!narrativeText) {
      return (
        <div className="scb-empty-state">This section isn't available yet.</div>
      );
    }
    return <SummaryCard paragraphs={splitIntoParagraphs(narrativeText)} />;
  };

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(STRATEGY_CANVAS)}
        showBack
      />
      <div id="strategy-canvas-brief">
        <div className="scb-header">
          <div className="scb-title">{companyName}</div>
          <button
            className="scb-cta-btn"
            disabled={ctaDisabled}
            onClick={cta.onClick}
          >
            {cta.label}
            <img
              src={"/assets/images/right-arrow-white.svg"}
              alt="arrow right icon"
              className="scb-cta-icon"
            />
          </button>
        </div>

        <div className="scb-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`scb-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="scb-content">
          {activeTab === TAB_SUMMARY ? (
            <>
              <div className="scb-subheader">
                <button
                  className="scb-download-link"
                  onClick={handleDownloadSummaryPdf}
                  disabled={
                    summaryLoading ||
                    summaryDownloading ||
                    !summary?.story?.story
                  }
                >
                  Download PDF
                  <DownloadIcon />
                </button>
              </div>
              {renderSummaryTab()}
            </>
          ) : activeTab === TAB_XRAY ? (
            <div className="scb-empty-card">
              <button
                className="scb-empty-icon"
                onClick={handleOpenXrayReport}
                disabled={xrayLoading || !xray?.user_id}
                aria-label="Open X-Ray Report"
              >
                <ExternalLinkIcon />
              </button>
              <div className="scb-empty-title">X-Ray Report</div>
              <div className="scb-empty-subtitle">
                {xrayLoading
                  ? "Loading X-Ray Report…"
                  : "Click the icon above to view the full X-Ray Report."}
              </div>
            </div>
          ) : activeTab === TAB_BUSINESS_CONTEXT ? (
            <>
              <div className="scb-subheader scb-subheader-split">
                <span className="scb-subheader-text">
                  This briefing is generated by AI from the documents CEO
                  provided. Read the originals in the{" "}
                  <button
                    className="scb-inline-link"
                    onClick={() => setActiveTab(TAB_DOCUMENTS)}
                  >
                    Documents tab →
                  </button>
                </span>
                <button className="scb-download-link" disabled>
                  Download PDF
                  <DownloadIcon />
                </button>
              </div>
              {renderBusinessContextTab()}
            </>
          ) : activeTab === TAB_CEO_CANVAS ? (
            <>
              <div className="scb-subheader">
                <button
                  className="scb-download-link"
                  onClick={handleDownloadCeoJourneyPdf}
                  disabled={
                    howCeoBuiltLoading ||
                    howCeoBuiltDownloading ||
                    !howCeoBuilt?.narrative ||
                    howCeoBuilt?.narrative?.is_placeholder
                  }
                >
                  Download PDF
                  <DownloadIcon />
                </button>
              </div>
              {renderCeoCanvasTab()}
            </>
          ) : activeTab === TAB_DOCUMENTS ? (
            renderDocumentsTab()
          ) : (
            <div className="scb-empty-state">
              No {activeTab.toLowerCase()} data yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyCanvasBrief;
